import { Icon, Switch, TextField, Typography, Box, Button, Card, CardContent, CircularProgress, Alert, Tab, Tabs, Chip } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { UserRoleEnum, useUserContext, usePushNotificationContext } from '../../shared/contexts';
import { LayoutShell, LayoutRegister } from '../../shared/layouts';
import { Api } from '../../shared/services/api/axios-config';
import { employeeService } from '../../shared/services';
import { Forbidden, IosPwaBanner } from '../../shared/components';

interface INotificationType {
  notificationType: string;
  label: string;
  description: string;
  role: string;
  enabled: boolean;
  configJson?: string;
  configSchema?: string;
  category?: string;
}

interface ICategoryMeta {
  key: string;
  label: string;
  icon: string;
}

const CATEGORIES: ICategoryMeta[] = [
  { key: 'appointments', label: 'Agendamentos', icon: 'calendar_today' },
  { key: 'financial', label: 'Financeiro', icon: 'payments' },
  { key: 'system', label: 'Sistema', icon: 'settings' },
];

const UNCATEGORIZED: ICategoryMeta = { key: '_other', label: 'Geral', icon: 'notifications' };

interface INotificationHistoryItem {
  id: number;
  userId: number;
  businessId?: number;
  title: string;
  body: string;
  clickUrl?: string;
  sentAt: string;
}

const Notifications: React.FC = () => {
  const { userId, userRoles } = useUserContext();
  const queryClient = useQueryClient();
  const [businessId, setBusinessId] = useState<number | null>(null);
  const [settings, setSettings] = useState<INotificationType[]>([]);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  // Descobre o businessId conforme o papel do usuário
  const { data: employeeData } = useQuery(
    ['employee', userId],
    () => employeeService.getByUserId(userId),
    { enabled: userRoles.includes(UserRoleEnum.Employee) }
  );

  const { data: adminData } = useQuery(
    ['businessAdmin', userId],
    () => Api.get(`/businessAdmin/user/${userId}`).then(r => r.data),
    { enabled: userRoles.includes(UserRoleEnum.BusinessAdmin) }
  );

  useEffect(() => {
    if (userRoles.includes(UserRoleEnum.BusinessAdmin) && adminData?.businessId) {
      setBusinessId(adminData.businessId);
    } else if (userRoles.includes(UserRoleEnum.Employee) && employeeData?.businessEmployees?.length) {
      setBusinessId(employeeData.businessEmployees[0].businessId);
    }
  }, [adminData, employeeData, userRoles]);

  // Carrega as settings da API
  const { isLoading } = useQuery(
    ['notificationSettings', businessId],
    () => Api.get<INotificationType[]>(`/notificationsettings?businessId=${businessId}`).then(r => r.data),
    {
      enabled: !!businessId,
      onSuccess: (data) => setSettings(data),
    }
  );

  // Histórico de notificações
  const { data: history, isLoading: isLoadingHistory } = useQuery(
    ['pushHistory', businessId],
    () => Api.get<INotificationHistoryItem[]>(`/push/history?businessId=${businessId}`).then(r => r.data),
    { enabled: !!businessId }
  );

  // Mutation para salvar
  const saveMutation = useMutation(
    (updates: { notificationType: string; enabled: boolean; configJson?: string }[]) =>
      Api.put(`/notificationsettings?businessId=${businessId}`, updates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notificationSettings', businessId]);
        setError('');
      },
      onError: () => setError('Erro ao salvar configurações.'),
    }
  );

  // Filtra os tipos conforme o papel do usuário
  const filteredSettings = settings.filter(s => {
    const roles = s.role.split(',');
    if (userRoles.includes(UserRoleEnum.BusinessAdmin) && roles.includes('admin')) return true;
    if (userRoles.includes(UserRoleEnum.Employee) && roles.includes('employee')) return true;
    return false;
  });

  // Agrupa por categoria, preservando a ordem das categorias definidas
  const groupedSettings = useMemo(() => {
    const groupKeys = CATEGORIES.map(c => c.key);
    const groups: { meta: ICategoryMeta; items: INotificationType[] }[] = [];

    for (const cat of CATEGORIES) {
      const items = filteredSettings.filter(s => s.category === cat.key);
      if (items.length > 0) groups.push({ meta: cat, items });
    }

    // Itens sem categoria conhecida vão para o final
    const other = filteredSettings.filter(s => !s.category || !groupKeys.includes(s.category));
    if (other.length > 0) groups.push({ meta: UNCATEGORIZED, items: other });

    return groups;
  }, [filteredSettings]);

  const roleBadge = (role: string) => {
    const roles = role.split(',');
    if (roles.includes('admin') && roles.includes('employee')) {
      return <Chip label="Admin + Profissional" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />;
    }
    if (roles.includes('admin')) {
      return <Chip label="Admin" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />;
    }
    if (roles.includes('employee')) {
      return <Chip label="Profissional" size="small" color="secondary" variant="outlined" sx={{ ml: 1 }} />;
    }
    return null;
  };

  const handleToggle = useCallback((notificationType: string, enabled: boolean) => {
    setSettings(prev =>
      prev.map(s => s.notificationType === notificationType ? { ...s, enabled } : s)
    );
  }, []);

  const handleConfigChange = useCallback((notificationType: string, value: string) => {
    setSettings(prev =>
      prev.map(s => s.notificationType === notificationType ? { ...s, configJson: value } : s)
    );
  }, []);

  const handleSave = useCallback(() => {
    const updates = settings.map(s => ({
      notificationType: s.notificationType,
      enabled: s.enabled,
      configJson: s.configJson,
    }));
    saveMutation.mutate(updates);
  }, [settings, saveMutation]);

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isAdmin = userRoles.includes(UserRoleEnum.BusinessAdmin);
  const isEmployee = userRoles.includes(UserRoleEnum.Employee);

  if (!isAdmin && !isEmployee) {
    return <Forbidden />;
  }

  return (
    <LayoutShell>
      <LayoutRegister
        header={
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 2 }}>
              <Icon>notifications</Icon>
              <Typography variant="h6">Notificações</Typography>
            </Box>
            <Tabs value={tabIndex} onChange={(_, i) => setTabIndex(i)} variant='fullWidth'>
              <Tab label='Configurações' icon={<Icon>settings</Icon>} iconPosition='start' />
              <Tab label='Histórico' icon={<Icon>history</Icon>} iconPosition='start' />
            </Tabs>
          </Box>
        }
        saveButtonLabel="Salvar"
        onClickSave={handleSave}
        disabled={saveMutation.isLoading}
        hideSaveButton={tabIndex === 1}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Aba: Configurações ── */}
        {tabIndex === 0 && (
          <>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!isLoading && groupedSettings.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Nenhuma configuração de notificação disponível para seu perfil.
              </Typography>
            )}

            {groupedSettings.map((group) => (
              <Box key={group.meta.key} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: group.meta.key === '_other' ? 0 : 1 }}>
                  <Icon sx={{ color: 'text.secondary', fontSize: 20 }}>{group.meta.icon}</Icon>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {group.meta.label}
                  </Typography>
                </Box>

                {group.items.map((s) => (
                  <Card key={s.notificationType} sx={{ mb: 1.5 }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {s.label}
                            </Typography>
                            {roleBadge(s.role)}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {s.description}
                          </Typography>
                        </Box>
                        <Switch
                          checked={s.enabled}
                          onChange={(_, checked) => handleToggle(s.notificationType, checked)}
                        />
                      </Box>

                      {/* Config extra (ex: reminderMinutes) */}
                      {s.enabled && s.configSchema && (
                        <Box sx={{ mt: 1.5, ml: 0 }}>
                          <ExtraConfig
                            schema={s.configSchema}
                            value={s.configJson}
                            onChange={(v) => handleConfigChange(s.notificationType, v)}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ))}

            {/* Se o usuário negou permissão, orienta como reativar */}
            <PushPermissionDeniedCard />
            <IosPwaBanner />
          </>
        )}

        {/* ── Aba: Histórico ── */}
        {tabIndex === 1 && (
          <>
            {isLoadingHistory && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!isLoadingHistory && (!history || history.length === 0) && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Icon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }}>notifications_off</Icon>
                <Typography color="text.secondary">
                  Nenhuma notificação enviada ainda.
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  As notificações push enviadas aparecerão aqui.
                </Typography>
              </Box>
            )}

            {history?.map((item) => (
              <Card key={item.id} sx={{ mb: 1.5 }}>
                <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                  <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
                    <Box flex={1} minWidth={0}>
                      <Typography variant='subtitle2' fontWeight={700} noWrap>
                        {item.title}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {item.body}
                      </Typography>
                    </Box>
                    <Chip
                      label={formatDateTime(item.sentAt)}
                      size='small'
                      variant='outlined'
                      sx={{ ml: 1, flexShrink: 0, fontSize: '0.7rem' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </LayoutRegister>
    </LayoutShell>
  );
};

/** Card exibido quando o usuário negou permissão de notificações. */
const PushPermissionDeniedCard: React.FC = () => {
  const { permissionState } = usePushNotificationContext();
  const [showHelp, setShowHelp] = useState(false);

  if (permissionState !== 'denied') return null;

  return (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          🔕 Notificações desativadas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Você negou a permissão de notificações para o SconApp.
          {!showHelp && (
            <Button size="small" sx={{ ml: 1 }} onClick={() => setShowHelp(true)}>
              Como reativar?
            </Button>
          )}
        </Typography>
        {showHelp && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>No iPhone/iPad (PWA):</strong>
            </Typography>
            <ol style={{ margin: 0, paddingLeft: '20px', marginBottom: 8 }}>
              <li>Abra <strong>Ajustes</strong> do iPhone</li>
              <li>Role até <strong>SconApp</strong> na lista de apps</li>
              <li>Toque em <strong>Notificações</strong></li>
              <li>Ative <strong>Permitir Notificações</strong></li>
            </ol>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>No Chrome (Windows/Android):</strong>
            </Typography>
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Clique no cadeado 🔒 na barra de endereço</li>
              <li>Em <strong>Notificações</strong>, selecione <strong>Permitir</strong></li>
            </ol>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/** Renderiza inputs para as configs extras baseado no schema JSON. */
const ExtraConfig: React.FC<{
  schema: string;
  value?: string;
  onChange: (value: string) => void;
}> = ({ schema, value, onChange }) => {
  let parsed: Record<string, { type: string; min?: number; max?: number; default?: number | string; format?: string }> = {};
  try {
    parsed = JSON.parse(schema);
  } catch {
    return null;
  }

  const keys = Object.keys(parsed);
  if (keys.length === 0) return null;

  const key = keys[0];
  const field = parsed[key];
  const currentVal = value ? tryParseJsonValue(value)?.[key] : undefined;

  if (field.type === 'number') {
    return (
      <TextField
        label="Minutos antes"
        type="number"
        size="small"
        inputProps={{ min: field.min ?? 5, max: field.max ?? 120 }}
        value={currentVal ?? field.default ?? 30}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          onChange(JSON.stringify({ [key]: v }));
        }}
        sx={{ maxWidth: 160 }}
      />
    );
  }

  if (field.type === 'string' && field.format === 'HH:mm') {
    return (
      <TextField
        label="Horário do resumo"
        type="time"
        size="small"
        value={currentVal ?? field.default ?? '07:00'}
        onChange={(e) => onChange(JSON.stringify({ [key]: e.target.value }))}
        sx={{ maxWidth: 160 }}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  return null;
};

function tryParseJsonValue(str: string): Record<string, unknown> | null {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

export { Notifications };